import React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { CourseGroupFieldApi, coursegroupsApi } from "api";
import { FormProvider, useForm } from "react-hook-form";

import { PaziaButton, PaziaFormButton, PaziaInput } from "components/atoms";
import { MdKeyboardArrowDown } from "react-icons/md";
import { useToaster } from "utils";
import { useQueryClient } from "@tanstack/react-query";
import { QUERIES_KEYS } from "queries";
import { PaziaFromQuill } from "components/atoms/PaziaQuill/PaziaFromQuill";
import { MaterialIconSelect } from "components/atoms";
import { htmlParser } from "utils/htmlParser";

interface SingleCourseGroupFieldForm {
  field: CourseGroupFieldApi;
  isExpanded: boolean;
  collapse: () => void;
  open: () => void;
  courseGroupId: number;
}
export const SingleCourseGroupFieldForm = ({
  courseGroupId,
  field,
  isExpanded,
  collapse,
  open,
}: SingleCourseGroupFieldForm) => {
  const { showError, showSuccess } = useToaster();
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: QUERIES_KEYS.COURSE_GROUP_BY_ID(Number(courseGroupId)),
    });
  };
  const methods = useForm<CourseGroupFieldFormValues>({
    resolver: yupResolver(CourseGroupFieldSchema),
    defaultValues: {
      name: field.name ?? "",
      content: field.content ?? "",
      icon: field.icon ?? "",
    },
  });

  const onSubmit = async (data: CourseGroupFieldFormValues) => {
    try {
      const requestData = { ...data, icon: data.icon ? `<span class="material-icons">${data.icon}</span>` : "" };
      await coursegroupsApi.updateField(courseGroupId, field.courseGroupFieldId, requestData);
      showSuccess("Zaktualizowano grupę kursów");
    } catch (error) {
      showError("Nie udało się zaktualizować grupy kursów");
    } finally {
      invalidate();
    }
  };

  const onRemove = async () => {
    try {
      await coursegroupsApi.deleteField(courseGroupId, field.courseGroupFieldId);
      showSuccess("Usunięto pole");
    } catch (error) {
      showError("Nie udało się usunąć pola");
    } finally {
      invalidate();
    }
  };

  const { handleSubmit } = methods;

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 dark:border-slate-600 dark:bg-slate-700/50">
      {/* Collapsed state */}
      <div
        className={`${isExpanded ? "hidden" : "flex"} cursor-pointer items-center justify-between gap-2 p-4 transition-colors hover:bg-slate-100 dark:hover:bg-slate-600/50`}
        onClick={() => open()}
      >
        <div className="flex items-center gap-3">
          <div className="w-5">{htmlParser.toHTML(field.icon ?? "")}</div>
          <div className="flex flex-col gap-1">
            <span className="font-medium text-slate-800 dark:text-slate-200">{field.name}</span>
            <span className="text-xs text-slate-500 dark:text-slate-400">
              Kliknij aby edytować • Utworzono {new Date(field.createdDate).toLocaleDateString("pl-PL")}
            </span>
          </div>
        </div>
        <button
          className="text-slate-400 transition-colors hover:text-slate-600 dark:hover:text-slate-200"
          type="button"
        >
          <MdKeyboardArrowDown size={20} />
        </button>
      </div>

      {/* Expanded state */}
      <div className={`${!isExpanded ? "hidden" : "block"}`}>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className="p-4">
            {/* Header with collapse button */}
            <div
              className="-m-2 mb-4 flex cursor-pointer items-center justify-between rounded-md p-2 transition-colors hover:bg-slate-100 dark:hover:bg-slate-600/50"
              onClick={() => collapse()}
            >
              <h3 className="text-lg font-medium text-slate-800 dark:text-slate-200">Edytuj pole</h3>
              <div className="text-slate-400 transition-colors hover:text-slate-600 dark:hover:text-slate-200">
                <svg className="h-5 w-5 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            {/* Form fields */}
            <div className="space-y-4">
              <PaziaInput label="Nazwa pola" name="name" className="w-full" />
              <MaterialIconSelect name="icon" label="Ikona" className="w-full" />
              <PaziaFromQuill name="content" />

              {/* Action buttons */}
              <div className="flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:justify-end dark:border-slate-600">
                <PaziaButton
                  type="danger"
                  btnType="button"
                  onClick={onRemove}
                  className="order-3 sm:order-1 sm:mr-auto"
                >
                  Usuń pole
                </PaziaButton>
                <button
                  type="button"
                  onClick={() => collapse()}
                  className="order-2 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
                >
                  Anuluj
                </button>
                <PaziaFormButton title="Zapisz zmiany" type="success" className="order-1 sm:order-3" />
              </div>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export const CourseGroupFieldSchema = yup.object({
  name: yup.string().required(),
  content: yup.string().nullable(),
  icon: yup.string().nullable(),
});

export type CourseGroupFieldFormValues = yup.InferType<typeof CourseGroupFieldSchema>;
